/**
 * SALES-FORECASTING MODULE
 * Self-contained. Import only from /shared or within this module.
 * Do not import from other modules directly.
 */

// ExportOptionsModal.jsx — options dialog shown before a spreadsheet export.
// Lets the user optionally add a chart worksheet and pick how that chart is
// grouped. `onConfirm(options)` may be async; the modal shows a busy state and
// surfaces any error before closing. If it resolves to a string (or
// { notice }), the file has already downloaded but something about it needs
// saying — the dialog holds open on that one line (backlog 330).

import React, { useState } from "react";
import "./ExportOptionsModal.css";

const GROUP_BY_OPTIONS = [
  { value: "sku",         label: "Product SKU" },
  { value: "region",      label: "Region" },
  { value: "state",       label: "State" },
  { value: "distributor", label: "Distributor" },
  { value: "size",        label: "Bottle Size" },
  { value: "packSize",    label: "Pack Size" },
  { value: "tag",         label: "TAG" },
  { value: "flavor",      label: "Flavor" },
];

export default function ExportOptionsModal({ open, onClose, onConfirm }) {
  const [includeChart, setIncludeChart] = useState(false);
  const [chartType, setChartType] = useState("line"); // "line" | "bar"
  const [chartGroupBy, setChartGroupBy] = useState("sku");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  if (!open) return null;

  const handleConfirm = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await onConfirm({ includeChart, chartType, chartGroupBy });
      const msg = typeof res === "string" ? res : res?.notice;
      if (msg) { setNotice(msg); setBusy(false); }
      else onClose();
    } catch (err) {
      console.error(err);
      setError(err?.message || "Export failed.");
      setBusy(false);
    }
  };

  return (
    <div className="exo-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="exo-card fc-module" role="dialog" aria-modal="true" aria-label="Export options">
        <div className="exo-head">
          <h3 className="exo-title">Export options</h3>
          <button className="exo-close" onClick={() => !busy && onClose()} aria-label="Close" disabled={busy}>×</button>
        </div>

        <div className="exo-body">
          <label className="exo-check">
            <input
              type="checkbox"
              checked={includeChart}
              onChange={(e) => setIncludeChart(e.target.checked)}
              disabled={busy}
            />
            <span className="exo-check-box" aria-hidden>
              {includeChart && (
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </span>
            <span>
              <span className="exo-check-label">Include chart</span>
              <span className="exo-check-note">Adds a second worksheet tab with a forecast chart.</span>
            </span>
          </label>

          <div className={`exo-field${includeChart ? "" : " exo-field-disabled"}`}>
            <label className="exo-field-label">Chart type</label>
            <div className="exo-typetoggle" role="group" aria-label="Chart type">
              <button
                type="button"
                className={`exo-type-btn${chartType === "line" ? " exo-type-btn-active" : ""}`}
                onClick={() => setChartType("line")}
                disabled={!includeChart || busy}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="22 12 18 12 15 20 9 4 6 12 2 12" />
                </svg>
                Line
              </button>
              <button
                type="button"
                className={`exo-type-btn${chartType === "bar" ? " exo-type-btn-active" : ""}`}
                onClick={() => setChartType("bar")}
                disabled={!includeChart || busy}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="4" height="18" /><rect x="10" y="8" width="4" height="13" /><rect x="17" y="13" width="4" height="8" />
                </svg>
                Bar
              </button>
            </div>
          </div>

          <div className={`exo-field${includeChart ? "" : " exo-field-disabled"}`}>
            <label className="exo-field-label" htmlFor="exo-groupby">Chart group by</label>
            <select
              id="exo-groupby"
              className="selector-select"
              value={chartGroupBy}
              onChange={(e) => setChartGroupBy(e.target.value)}
              disabled={!includeChart || busy}
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {error && <div className="exo-error">{error}</div>}
          {notice && <div className="exo-notice">{notice}</div>}
        </div>

        <div className="exo-foot">
          {notice ? (
            <button className="exo-btn exo-btn-go" onClick={onClose}>Done</button>
          ) : (
            <>
              <button className="exo-btn exo-btn-cancel" onClick={() => !busy && onClose()} disabled={busy}>Cancel</button>
              <button className="exo-btn exo-btn-go" onClick={handleConfirm} disabled={busy}>
                {busy ? "Exporting…" : "Export"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
